import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, coverUri, resolveFileUrl, uploadChat, fileKind } from "../lib/api";
import { fmtTime, fmtClock, initials } from "../lib/format";
import { useAuth } from "../auth";
import { Spinner, EmptyState } from "../components/ui";
import { Icon } from "../components/icons";
import { ChatAssistant } from "../components/Assistant";
import { MessageMenu, useLongPress } from "../components/MessageMenu";
import { ChatMembers, RoomMenu } from "../components/ChatPeople";
import { isSystemAdmin, can } from "../lib/permissions";

function Attachment({ m, playingId, onToggleAudio }) {
  const url = resolveFileUrl(m.file_url || "");
  const open = (download) => {
    if (!url) return;
    window.open(download ? `${url}${url.includes("?") ? "&" : "?"}download=1` : url, "_blank", "noopener");
  };
  if (m.type === "image" && m.file_url) {
    return (
      <button onClick={() => open(false)} style={{ padding: 0, border: "none", background: "none", cursor: "pointer", display: "block" }} data-testid={`chat-image-${m.id}`}>
        <img src={url} alt={m.filename || "imagem"} style={{ maxWidth: 240, maxHeight: 260, borderRadius: 12, display: "block", objectFit: "cover" }} />
      </button>
    );
  }
  if (m.type === "audio" && m.file_url) {
    const playing = playingId === m.id;
    return (
      <button onClick={() => onToggleAudio(m)} aria-label="Reproduzir nota de voz" data-testid={`chat-audio-${m.id}`} style={{ display: "flex", alignItems: "center", gap: 10, background: "none", border: "none", cursor: "pointer", color: "inherit", padding: 0 }}>
        <span style={{ width: 38, height: 38, borderRadius: 999, background: "var(--brand-primary)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Icon name={playing ? "pause" : "play"} size={18} color="var(--on-brand)" />
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <Icon name="mic" size={15} />
          <span style={{ fontVariantNumeric: "tabular-nums" }}>{m.duration_ms ? fmtClock(m.duration_ms / 1000) : "Áudio"}</span>
        </span>
      </button>
    );
  }
  if (m.type === "file" && m.file_url) {
    return (
      <button onClick={() => open(true)} aria-label="Abrir anexo" data-testid={`chat-file-${m.id}`} style={{ display: "flex", alignItems: "center", gap: 10, background: "none", border: "none", cursor: "pointer", color: "inherit", padding: 0 }}>
        <span style={{ width: 38, height: 38, borderRadius: 10, background: "var(--surface-3)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Icon name="document" size={18} color="var(--brand-primary)" />
        </span>
        <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
          <span style={{ fontFamily: "var(--f-medium)", fontSize: 14 }}>{m.filename || "Ficheiro"}</span>
          <span className="t-hint">Tocar para transferir</span>
        </span>
      </button>
    );
  }
  return null;
}

export default function Chat() {
  const { room } = useParams();
  const roomsQ = useQuery({ queryKey: ["chat-rooms"], queryFn: () => api("/chat/rooms") });
  const roomTitle = (roomsQ.data || []).find((r) => r.room === decodeURIComponent(room))?.title || (roomsQ.isLoading ? "" : decodeURIComponent(room));
  const navigate = useNavigate();
  const { user } = useAuth();
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const [progress, setProgress] = useState(null);
  const [notice, setNotice] = useState("");
  const [playingId, setPlayingId] = useState(null);
  const endRef = useRef(null);
  const fileRef = useRef(null);
  const audioRef = useRef(null);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const startedAtRef = useRef(0);
  const [recording, setRecording] = useState(false);
  const [recSecs, setRecSecs] = useState(0);
  const [assistant, setAssistant] = useState(false);
  const timerRef = useRef(null);

  const q = useQuery({
    queryKey: ["chat", room],
    queryFn: () => api(`/chat/${encodeURIComponent(room)}/messages?limit=100`),
    refetchInterval: 4000,
  });
  const [menuFor, setMenuFor] = useState(null);
  const moderator = isSystemAdmin(user) || can(user, "chat.moderate");
  const longPress = useLongPress((m) => setMenuFor(m));
  const del = useMutation({
    mutationFn: (m) => api(`/chat/${encodeURIComponent(room)}/messages/${m.id}`, { method: "DELETE" }),
    onMutate: (m) => qc.setQueryData(["chat", room], (old) => (Array.isArray(old) ? old.filter((x) => x.id !== m.id) : old)),
    onError: (e) => { setNotice(e.message); q.refetch(); },
    onSettled: () => q.refetch(),
  });
  const [editing, setEditing] = useState(null);
  const edit = useMutation({
    mutationFn: ({ id, t }) => api(`/chat/${encodeURIComponent(room)}/messages/${id}`, { method: "PATCH", body: JSON.stringify({ text: t }) }),
    onSuccess: () => { setText(""); setEditing(null); q.refetch(); },
    onError: (e) => setNotice(e.message),
  });
  const roomAction = (method, path) => api(`/chat/${encodeURIComponent(room)}${path}`, { method }).then(() => { q.refetch(); qc.invalidateQueries({ queryKey: ["chat-rooms"] }); }).catch((e) => setNotice(e.message));
  const raw = Array.isArray(q.data) ? q.data : q.data?.messages || [];
  // API returns newest-first in the Mobile app; show oldest-first.
  const messages = [...raw].reverse();

  useEffect(() => {
    api(`/chat/${encodeURIComponent(room)}/read`, { method: "POST" }).catch(() => {});
    qc.invalidateQueries({ queryKey: ["chat-rooms"] });
  }, [room, messages.length, qc]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  const send = useMutation({
    mutationFn: (body) => api(`/chat/${encodeURIComponent(room)}/messages`, { method: "POST", body: JSON.stringify({ text: body }) }),
    onSuccess: () => { setText(""); q.refetch(); },
  });

  const submit = (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t) return;
    if (editing) edit.mutate({ id: editing.id, t });
    else send.mutate(t);
  };

  const audioDuration = (file) =>
    new Promise((resolve) => {
      try {
        const a = document.createElement("audio");
        a.preload = "metadata";
        a.onloadedmetadata = () => resolve(Number.isFinite(a.duration) ? a.duration * 1000 : 0);
        a.onerror = () => resolve(0);
        a.src = URL.createObjectURL(file);
      } catch {
        resolve(0);
      }
    });

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = "";
    if (!file) return;
    const kind = fileKind(file);
    const durationMs = kind === "audio" ? await audioDuration(file) : 0;
    setNotice("");
    setProgress(0);
    try {
      await uploadChat(room, file, kind, durationMs, setProgress);
      q.refetch();
    } catch (err) {
      setNotice(err.message);
    } finally {
      setProgress(null);
    }
  };

  const startRecording = async () => {
    setNotice("");
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      setNotice("Gravação de áudio não suportada neste navegador.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      mr.ondataavailable = (ev) => ev.data.size && chunksRef.current.push(ev.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        clearInterval(timerRef.current);
        const durationMs = Date.now() - startedAtRef.current;
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        setRecording(false);
        setRecSecs(0);
        if (blob.size < 500) return;
        const ext = (mr.mimeType || "audio/webm").includes("mp4") ? "m4a" : "webm";
        const file = new File([blob], `nota-de-voz-${Date.now()}.${ext}`, { type: blob.type });
        setProgress(0);
        try {
          await uploadChat(room, file, "audio", durationMs, setProgress);
          q.refetch();
        } catch (err) {
          setNotice(err.message);
        } finally {
          setProgress(null);
        }
      };
      recRef.current = mr;
      startedAtRef.current = Date.now();
      mr.start();
      setRecording(true);
      setRecSecs(0);
      timerRef.current = setInterval(() => setRecSecs((s) => s + 1), 1000);
    } catch {
      setNotice("Não foi possível aceder ao microfone. Verifique as permissões.");
    }
  };

  const stopRecording = (cancel) => {
    const mr = recRef.current;
    if (!mr) return;
    if (cancel) {
      mr.onstop = () => {
        mr.stream?.getTracks?.().forEach((t) => t.stop());
        clearInterval(timerRef.current);
        setRecording(false);
        setRecSecs(0);
      };
    }
    try { mr.stop(); } catch (err) { console.error("Falha ao parar a gravação", err); }
  };

  useEffect(() => () => {
    const mr = recRef.current;
    if (mr) {
      mr.onstop = null;
      if (mr.state !== "inactive") mr.stop();
      mr.stream?.getTracks?.().forEach((t) => t.stop());
    }
    clearInterval(timerRef.current);
    audioRef.current?.pause();
  }, []);

  const toggleAudio = (m) => {
    if (!audioRef.current) audioRef.current = new Audio();
    const a = audioRef.current;
    if (playingId === m.id) {
      a.pause();
      setPlayingId(null);
      return;
    }
    a.src = resolveFileUrl(m.file_url || "");
    a.onended = () => setPlayingId(null);
    a.play().catch(() => {});
    setPlayingId(m.id);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div className="page-header" style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <button className="icon-btn" aria-label="Voltar" onClick={() => navigate(-1)}><Icon name="chevron-back" size={22} /></button>
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
          <h1 className="t-h1" style={{ fontSize: 22, textTransform: "capitalize" }} data-testid="chat-room-title">{roomTitle}</h1>
          <ChatMembers room={room} me={user} />
        </div>
        <RoomMenu room={room} moderator={moderator} onClearMe={() => roomAction("POST", "/clear")} onClearAll={() => roomAction("DELETE", "/messages")} />
      </div>
      {assistant ? <ChatAssistant context={`Conversa do naipe/sala: ${decodeURIComponent(room)}.`} onClose={() => setAssistant(false)} /> : null}

      <div style={{ flex: 1, overflowY: "auto", padding: "16px 24px", maxWidth: 820, width: "100%", margin: "0 auto" }} data-testid="chat-messages" role="log" aria-live="polite" aria-label="Mensagens do naipe">
        {q.isLoading ? (
          <Spinner />
        ) : !messages.length ? (
          <EmptyState icon="chatbubbles-outline" text="Ainda não há mensagens. Seja o primeiro a escrever." />
        ) : (
          messages.map((m, i) => {
            const mine = m.mine ?? (m.user_id && user?.id && m.user_id === user.id);
            const hasAttachment = m.type && m.type !== "text";
            const canDelete = !!m.id && (m.can_delete ?? (mine || moderator));
            const canEdit = !!m.id && !!m.can_edit;
            return (
              <div key={m.id || i} className="chat-msg" style={{ display: "flex", gap: 10, marginBottom: 14, flexDirection: mine ? "row-reverse" : "row", alignItems: "flex-start" }} data-testid={`chat-message-${m.id}`}>
                <div className="avatar" style={{ width: 34, height: 34, fontSize: 12 }}>
                  {coverUri(m.avatar || m.user_photo_url) ? <img src={coverUri(m.avatar || m.user_photo_url)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : initials(m.user_name)}
                </div>
                <div style={{ maxWidth: "74%" }}>
                  {!mine ? <div style={{ color: "var(--muted)", fontSize: 11, fontFamily: "var(--f-medium)", marginBottom: 3 }}>{(m.user_name || "").toUpperCase()}{m.user_role === "admin" ? " · ADMIN" : ""}</div> : null}
                  <div {...(canDelete || canEdit ? longPress(m) : {})} style={{ WebkitUserSelect: canDelete || canEdit ? "none" : undefined, background: mine ? "var(--brand-primary)" : "var(--surface-2)", color: mine ? "var(--on-brand)" : "var(--on-surface)", padding: hasAttachment && m.type === "image" ? 4 : "9px 13px", borderRadius: 14, fontSize: 15, lineHeight: 1.4, border: mine ? "none" : "1px solid var(--border)" }}>
                    {hasAttachment ? <Attachment m={m} playingId={playingId} onToggleAudio={toggleAudio} /> : m.text}
                  </div>
                  <div style={{ color: "var(--muted)", fontSize: 10, marginTop: 3, textAlign: mine ? "right" : "left" }}>
                    {m.created_at ? fmtTime(m.created_at) : ""}{m.edited ? " · editada" : ""}
                  </div>
                </div>
                {canDelete || canEdit ? (
                  <button className="icon-btn msg-menu-btn" aria-label="Opções da mensagem" onClick={() => setMenuFor(m)} data-testid={`chat-message-menu-${m.id}`} style={{ width: 30, height: 30, alignSelf: "center" }}>
                    <Icon name="ellipsis" size={16} />
                  </button>
                ) : null}
              </div>
            );
          })
        )}
        <div ref={endRef} />
      </div>

      {progress !== null ? (
        <div style={{ maxWidth: 820, width: "100%", margin: "0 auto", padding: "0 24px 8px" }} data-testid="chat-upload-progress">
          <div className="track"><span style={{ width: `${Math.round(progress * 100)}%` }} /></div>
          <div className="t-hint" style={{ marginTop: 4 }}>A enviar anexo… {Math.round(progress * 100)}%</div>
        </div>
      ) : null}
      {notice ? <div style={{ maxWidth: 820, width: "100%", margin: "0 auto", padding: "0 24px 8px", color: "var(--on-error)" }}>{notice}</div> : null}

      {menuFor ? <MessageMenu message={menuFor} mine={menuFor.user_id === user?.id} canEdit={!!menuFor.can_edit} canDelete={menuFor.can_delete ?? (menuFor.user_id === user?.id || moderator)} onClose={() => setMenuFor(null)} onEdit={() => { setEditing(menuFor); setText(menuFor.text || ""); setMenuFor(null); }} onDelete={() => { del.mutate(menuFor); setMenuFor(null); }} /> : null}
      {editing ? (
        <div className="row-between" style={{ padding: "6px 16px", background: "var(--surface-2)", fontSize: 13 }} data-testid="chat-editing-bar">
          <span><Icon name="pencil" size={13} /> A editar mensagem</span>
          <button className="chip" onClick={() => { setEditing(null); setText(""); }} data-testid="chat-edit-cancel">CANCELAR</button>
        </div>
      ) : null}
      <form onSubmit={submit} className="chat-composer" style={{ borderTop: "1px solid var(--divider)", padding: 10, display: "flex", gap: 8, maxWidth: 820, width: "100%", margin: "0 auto", alignItems: "center" }}>
        <input ref={fileRef} type="file" accept="image/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.txt" hidden onChange={onPickFile} data-testid="chat-file-input" />
        {recording ? (
          <>
            <button type="button" className="icon-btn" onClick={() => stopRecording(true)} aria-label="Cancelar gravação" data-testid="chat-record-cancel" style={{ width: 46, height: 46, flexShrink: 0 }} title="Cancelar">
              <Icon name="trash" size={20} color="var(--muted)" />
            </button>
            <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 10, height: 52, padding: "0 16px", borderRadius: "var(--r-pill)", background: "var(--surface-2)", border: "1px solid var(--border)" }} data-testid="chat-recording">
              <span style={{ width: 10, height: 10, borderRadius: 999, background: "var(--brand-primary)", animation: "ulc-spin 1s linear infinite" }} />
              <span style={{ fontFamily: "var(--f-medium)", fontVariantNumeric: "tabular-nums" }}>A gravar · {fmtClock(recSecs)}</span>
            </div>
            <button type="button" className="icon-btn" aria-label="Enviar nota de voz" data-testid="chat-record-send" onClick={() => stopRecording(false)} style={{ background: "var(--brand-primary)", width: 46, height: 46, flexShrink: 0 }} title="Enviar nota de voz">
              <Icon name="send" size={20} color="var(--on-brand)" />
            </button>
          </>
        ) : (
          <>
            <button type="button" className="icon-btn" aria-label="Anexar ficheiro" data-testid="chat-attach" onClick={() => fileRef.current?.click()} style={{ width: 44, height: 44, flexShrink: 0, background: "var(--surface-2)", border: "1px solid var(--border)" }} title="Anexar ficheiro">
              <Icon name="add" size={20} />
            </button>
            <button type="button" className="icon-btn" aria-label="Assistente" data-testid="chat-assistant-toggle" onClick={() => setAssistant(true)} style={{ width: 44, height: 44, flexShrink: 0, background: "var(--surface-2)", border: "1px solid var(--border)" }} title="Assistente">
              <Icon name="sparkles" size={18} color="var(--brand-primary)" />
            </button>
            <input className="input" aria-label="Mensagem" data-testid="chat-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Escreva uma mensagem…" style={{ flex: 1, minWidth: 0 }} />
            {text.trim() ? (
              <button type="submit" className="icon-btn" aria-label="Enviar mensagem" data-testid="chat-send" style={{ background: "var(--brand-primary)", width: 46, height: 46, flexShrink: 0 }} disabled={send.isPending}>
                <Icon name="send" size={20} color="var(--on-brand)" />
              </button>
            ) : (
              <button type="button" className="icon-btn" aria-label="Gravar nota de voz" data-testid="chat-record" onClick={startRecording} style={{ background: "var(--brand-primary)", width: 46, height: 46, flexShrink: 0 }} title="Gravar nota de voz">
                <Icon name="mic" size={20} color="var(--on-brand)" />
              </button>
            )}
          </>
        )}
      </form>
    </div>
  );
}
